/**
 * Generic fallback form for any slide whose slug doesn't match a known
 * layout. Exposes the common slide fields plus a raw ``bodyHtml``
 * textarea — enough to edit any slide, just without layout-aware hints.
 */

import type { Slide, SlideUpdatePayload } from "../types";
import { CommonSlideFields } from "./CommonSlideFields";
import { LayoutHint } from "./LayoutHint";
import { TextArea } from "./FormPrimitives";

interface GenericFormProps {
  draft: Slide;
  setField<K extends keyof SlideUpdatePayload>(
    key: K,
    value: SlideUpdatePayload[K],
  ): void;
  flush(): void;
}

export function GenericForm({ draft, setField, flush }: GenericFormProps) {
  return (
    <div data-testid="layout-form-generic">
      <LayoutHint title="Generic slide">
        Slide slug doesn't match a known layout (opener, two-col, paths,
        asks). The form below edits the raw fields directly — useful for
        bespoke slides or new layouts the editor doesn't recognise yet.
      </LayoutHint>
      <CommonSlideFields draft={draft} setField={setField} flush={flush} />
      <TextArea
        label="Body HTML"
        value={draft.bodyHtml}
        onChange={(v) => setField("bodyHtml", v)}
        onBlur={flush}
        rows={14}
        testId="slide-body-html-input"
      />
    </div>
  );
}
